import useAnalyticsContext from '@hooks/useAnalyticsContext';
import {
	CATEGORY_FILTER_ID,
	type FacetMultiSelectFilterValue,
	type FilterValue,
	classifierSearchStringAtom,
	classifierSelectedCategoryIdsAtom,
	classifierSsrSyncAtom,
	classifierSyncAtom,
	classifierSyncGenerationAtom,
	filterConfigsAtom,
	filterValuesAtom,
} from '@pages/VendorsSearch/containers/FilterPills/atoms';
import {
	PRESERVE_CATEGORY_QUERY_PARAM,
	computeClassifierSyncKey,
	resolveClassifierCategoryOptions,
} from '@pages/VendorsSearch/hooks/classifier-sync-helpers';
import { crossCategoryMarketplaceSearchSelector } from '@redux/experiments/selectors/cross-category-marketplace-search';
import { useAtomValue, useSetAtom, useStore } from 'jotai';
import type { MutableRefObject } from 'react';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import type { State } from 'types/redux';
import type { Params } from '../containers/WrappedVendorsSearch';

const SEMANTIC_SUGGESTION_CATEGORY_QUERY_PARAM =
	'semanticSuggestionCategoryCode';

interface UseClassifierCategorySyncOptions {
	/**
	 * Counter decremented by the parent search-fetch effect. When greater than
	 * zero the effect skips one invocation and decrements. Set to 2 for a
	 * single-category sync from a single-category page (URL path change triggers
	 * `searchQueryParams` change, then `CATEGORY_LOADED` triggers `categoryCode`
	 * change), set to 3 when syncing from the all-categories page (no
	 * `params.categorySlug`), and set to 1 for a multi-category sync (only
	 * `searchQueryParams` changes).
	 */
	skipNextFetchRef: MutableRefObject<number>;
}

/**
 * Reads `selectedCategories` (category GUIDs) from the Redux search response,
 * compares them with the current page category, and — when the cross-category
 * experiment is active and the classifier has changed the category selection —
 * pre-selects the classifier categories in the filter UI and updates the URL
 * via `history.replace` (React Router) without triggering a full navigation or
 * re-fetch.
 *
 * The hook is intentionally idempotent: once it has applied a given
 * `selectedCategories` value it will not re-apply it unless the value changes
 * (e.g. after the user triggers a new search).
 *
 * Flow:
 * 1. Sets `classifierSyncAtom = true` so the FilterPills `urlFilterParams`
 *    effect skips its own `history.push`.
 * 2. Sets `skipNextFetchRef.current` to a numeric counter (`1` for multiple
 *    categories, `2` for single category when starting on a category page, or
 *    `3` when starting on the all-categories page) so WrappedVendorsSearch's
 *    fetch effect skips the appropriate invocation(s), and bumps
 *    `classifierSyncGenerationAtom` so `useVendorCategoryViewedTracking` knows
 *    no fetch is coming for the resulting view transition.
 * 3. Updates `filterValuesAtom` with the classifier-selected categories, which
 *    drives the filter-pill UI to show them as pre-selected.
 * 4. Calls `history.replace` to update the URL bar via React Router. This may
 *    trigger a route pattern change → component remount. The module-level
 *    idempotency guard ensures the second effect run (if any) returns early.
 * 5. Emits an analytics event (Classifier Category Selected).
 */

/**
 * Idempotency guard: module-level variable that persists across component remounts.
 *
 * Problem: When navigating from all-categories `/marketplace/location` to a
 * single-category page `/marketplace/{categorySlug}-{location}`, React Router
 * remounts the component, which resets any `useRef` hooks to their initial value.
 * This allowed the effect to fire twice with the same `selectedCategories`,
 * emitting duplicate track events.
 *
 * Solution: Store the last-applied state in a module-level variable instead of
 * a `useRef`. It persists across remounts, so the second run correctly detects
 * `lastAppliedClassifierSync === serialized` and returns early.
 *
 * Limitation: The variable is global to the hook module, so it's not scoped to
 * individual component instances. In practice, only one marketplace search page
 * is ever active at a time, so this is acceptable. If multiple search pages
 * existed simultaneously, a scoped approach (e.g., keyed by `locationSlug`)
 * would be needed.
 */
let lastAppliedClassifierSync: string | null = null;

/**
 * True when selectedCategories is temporarily unavailable because the backend
 * has not returned classifier categories yet (`null` during fetch transition).
 * Cleared the next time selectedCategories is available (including an empty
 * array, which is a valid backend response meaning "no classifier categories").
 *
 * Used to distinguish between two cases that both go through null:
 *   1. User-triggered re-fetch (filter removal, pagination, etc.) — the
 *      subsequent selectedCategories value matches lastAppliedClassifierSync,
 *      so we should NOT re-apply (the user intentionally removed the filter).
 *   2. New, different search result — the subsequent selectedCategories value
 *      differs from lastAppliedClassifierSync, so we SHOULD re-apply.
 */
let pendingReset = false;

/**
 * Resets the module-level idempotency guard. For use in tests only.
 * @internal
 */
export const resetClassifierSyncGuard = () => {
	lastAppliedClassifierSync = null;
	pendingReset = false;
};

export const useClassifierCategorySync = ({
	skipNextFetchRef,
}: UseClassifierCategorySyncOptions) => {
	const { track } = useAnalyticsContext();
	const params: Params = useParams();
	const jotaiStore = useStore();

	const isClassifierActive = true;
	const categoryId = useSelector((state: State) => state.category.id);
	const categoryCode = useSelector((state: State) => state.category.code);
	const history = useHistory();
	const selectedCategories = useSelector(
		(state: State) => state.search.selectedCategories,
	);
	const experimentAssignment = useSelector(
		crossCategoryMarketplaceSearchSelector,
	);

	const filterConfigs = useAtomValue(filterConfigsAtom);
	const setFilterValues = useSetAtom(filterValuesAtom);

	// lastAppliedClassifierSync is module-level (see above) — stable across remounts.

	useEffect(() => {
		// Guard: classifier must be active (variant2/variant3 only — variant1
		// gets the cross-category UI without auto-selected categories)
		if (!isClassifierActive) {
			return;
		}

		// Guard: backend must have returned selectedCategories.
		// Mark that we are in a "pending reset" state so that when selectedCategories
		// returns we can determine whether it is a genuinely new search result
		// (serialized differs → reset guard and re-apply) or a re-fetch triggered
		// by the user removing filters (serialized matches → skip re-application).
		// IMPORTANT: an empty array is a valid backend value meaning "no classifier
		// categories", so it should not be treated the same as null.
		if (selectedCategories === null) {
			pendingReset = true;
			jotaiStore.set(classifierSelectedCategoryIdsAtom, null);
			return;
		}

		// Guard: category filter config must be present (cross-category feature enabled)
		if (!(CATEGORY_FILTER_ID in filterConfigs)) {
			jotaiStore.set(classifierSelectedCategoryIdsAtom, null);
			return;
		}

		// Idempotency key: selectedCategories + locationSlug + semantic query text
		// (NOT categoryId).
		// categoryId is intentionally excluded because after history.replace triggers
		// CATEGORY_LOADED, Redux updates categoryId to the new value. If categoryId were
		// in the key, the second effect run would compute a different serialized value
		// than the first, bypassing the idempotency guard.
		const searchParams = new URLSearchParams(window.location.search);
		const searchQuery = searchParams.get('q') ?? '';
		const semanticSuggestionCategoryCode = searchParams.get(
			SEMANTIC_SUGGESTION_CATEGORY_QUERY_PARAM,
		);
		const preserveCategory = searchParams.get(PRESERVE_CATEGORY_QUERY_PARAM);
		const serialized = computeClassifierSyncKey(
			selectedCategories,
			params.locationSlug ?? '',
			searchQuery,
		);

		// Resolve the pending-reset state set when selectedCategories was null.
		// If the new serialized value differs from the last applied, this is a
		// genuinely new search result — reset the guard so re-application proceeds.
		// If the serialized value matches, this is a re-fetch triggered by the user
		// modifying filters (e.g. removing a category pill or pressing Clear All)
		// while the same search query was still active. In that case do NOT reset the
		// guard; the idempotency check below will skip re-application and preserve the
		// user's explicit filter removal.
		if (pendingReset) {
			pendingReset = false;
			if (serialized !== lastAppliedClassifierSync) {
				lastAppliedClassifierSync = null;
			}
		}

		// Typeahead semantic suggestions include an explicit category chosen at click
		// time, and a same-search location change (see LocationHeader) marks its
		// destination with PRESERVE_CATEGORY_QUERY_PARAM. Either way, the category
		// state already reflects an explicit choice for this exact query — keep it
		// and prevent the classifier from overriding it on this load (GS-1468).
		if (semanticSuggestionCategoryCode || preserveCategory) {
			lastAppliedClassifierSync = serialized;
			jotaiStore.set(classifierSelectedCategoryIdsAtom, null);
			searchParams.delete(SEMANTIC_SUGGESTION_CATEGORY_QUERY_PARAM);
			searchParams.delete(PRESERVE_CATEGORY_QUERY_PARAM);
			const nextQuery = searchParams.toString();
			history.replace(
				`${window.location.pathname}${nextQuery ? `?${nextQuery}` : ''}`,
			);
			return;
		}

		// Guard: skip if we already applied this exact combination
		if (lastAppliedClassifierSync === serialized) {
			return;
		}

		// SSR already resolved and seeded this exact classifier result (see
		// seedClassifierState.ts) — filterValuesAtom and
		// classifierSelectedCategoryIdsAtom are already hydrated with it, so
		// re-applying here would just repeat work and trigger a redundant
		// history.replace/re-render for a page that's already correct. Only
		// suppress the one extra search-fetch invocation the resulting Redux
		// category sync (WrappedVendorsSearch's topCategory effect, reacting to
		// the now-hydrated filterValuesAtom) causes, and fire the attribution
		// event SSR couldn't fire itself (analytics is client-only).
		// Never set for single-category slug pages, so this is a no-op there.
		const ssrSync = jotaiStore.get(classifierSsrSyncAtom);
		if (ssrSync && ssrSync.key === serialized) {
			lastAppliedClassifierSync = serialized;
			jotaiStore.set(classifierSearchStringAtom, searchQuery);
			skipNextFetchRef.current = 1;
			jotaiStore.set(
				classifierSyncGenerationAtom,
				(generation) => generation + 1,
			);
			track({
				event: 'Classifier Category Selected',
				properties: {
					action: 'classifier_category_selected',
					classifierVariant: experimentAssignment ?? null,
					originalCategoryCode: ssrSync.originalCategoryCode,
					classifierSelectedCategories: selectedCategories,
				},
			});
			return;
		}

		// Guard: skip if the classifier returned the same single category as the
		// current page (no change needed). Only apply this guard on actual
		// single-category pages (where params.categorySlug exists). On the
		// all-categories page (/marketplace/location), Redux category defaults to REC,
		// but we should still sync if the classifier selects REC — it's not the current
		// page, it's the default. Proceed with the sync so filters and URL get updated.
		if (
			params.categorySlug &&
			selectedCategories.length === 1 &&
			selectedCategories[0] === categoryId
		) {
			// Preserve classifier-origin context even when no UI/URL sync is needed,
			// so downstream analytics can still report was_auto_selected correctly.
			jotaiStore.set(classifierSelectedCategoryIdsAtom, selectedCategories);
			if (jotaiStore.get(classifierSearchStringAtom) !== searchQuery) {
				jotaiStore.set(classifierSearchStringAtom, searchQuery);
				skipNextFetchRef.current = 1;
			}
			lastAppliedClassifierSync = serialized;
			return;
		}

		// Look up filterOption objects by GUID. Each option has id, name, and
		// singular.slug — no code lookup needed since the backend now returns GUIDs.
		const categoryFilterOptions =
			filterConfigs[CATEGORY_FILTER_ID].filterOptions;
		const resolvedOptions = resolveClassifierCategoryOptions(
			selectedCategories,
			filterConfigs,
		);

		if (!resolvedOptions) {
			// Could not resolve any (or only some) category options — mark as
			// applied so the effect stays idempotent and doesn't re-run on every
			// render. A partial resolution would put the UI/URL out of sync with
			// the actual result set the backend searched for, so it's treated the
			// same as no resolution at all.
			jotaiStore.set(classifierSelectedCategoryIdsAtom, null);
			lastAppliedClassifierSync = serialized;
			return;
		}

		// 1. Set the classifierSyncAtom BEFORE updating filterValues so that the
		//    FilterPills urlFilterParams effect can detect and skip its history.push.
		//    We use the store directly (instead of setClassifierSync) to ensure the
		//    atom is updated synchronously in the same tick as the steps below.
		jotaiStore.set(classifierSyncAtom, true);

		// 2. Signal the parent search-fetch effect to skip upcoming invocations.
		// Single-category from a single-category page: history.replace changes
		// params.categorySlug → fires category-sync effect → dispatches CATEGORY_LOADED
		// → updates categoryCode → triggers fetch effect a second time. Skip 2 runs.
		// Single-category from all-categories page (params.categorySlug undefined):
		// history.replace changes params.categorySlug, isAllCategoriesPage flips,
		// and CATEGORY_LOADED fires, triggering the fetch effect three times.
		// Skip 3 runs.
		// Multi-category: only searchQueryParams changes (params.categorySlug is
		// unchanged, no CATEGORY_LOADED dispatch). Skip 1 run.
		skipNextFetchRef.current =
			selectedCategories.length === 1 ? (params.categorySlug ? 2 : 3) : 1;

		// 2b. Bump the classifier-sync generation counter so
		// useVendorCategoryViewedTracking knows the upcoming view-transition(s)
		// are classifier-driven and no isFetching cycle will occur for them.
		jotaiStore.set(
			classifierSyncGenerationAtom,
			(generation) => generation + 1,
		);

		// 3. Record which category GUIDs the classifier selected so downstream
		//    analytics can report `was_auto_selected`.
		jotaiStore.set(classifierSelectedCategoryIdsAtom, selectedCategories);
		jotaiStore.set(classifierSearchStringAtom, searchQuery);

		// Capture the original category codes from filterValues BEFORE step 4
		// overwrites them. On multi-category pages reached via a search redirect
		// from another multi-category page, the redirect URL preserves ?category=
		// params so filterValues is pre-populated with the original selection.
		// These codes are used for originalCategoryCode in the tracking event below.
		const rawFilterValues = jotaiStore.get(filterValuesAtom);
		const preUpdateFilterValues: FilterValue[] = Array.isArray(rawFilterValues)
			? rawFilterValues
			: [];
		const originalCategoryFilter = preUpdateFilterValues.find(
			(fv) => fv.id === CATEGORY_FILTER_ID,
		);
		const originalCategoryIds = Array.isArray(originalCategoryFilter?.value)
			? (originalCategoryFilter.value as FacetMultiSelectFilterValue).map(
					(v) => v.facetId,
				)
			: [];
		const maybeOriginalCategoryCodes = originalCategoryIds.map(
			(id) => categoryFilterOptions.find((opt) => opt.id === id)?.code,
		);

		// Guard: only emit originalCategoryCode if ALL pre-selected IDs resolved to codes.
		// Partial resolution (some IDs matched, some didn't) would emit an incomplete
		// originalCategoryCode that doesn't reflect the true original selection.
		// Same pattern as the resolvedOptions partial-resolution guard above.
		const originalCategoryCodes = maybeOriginalCategoryCodes.every(
			(code): code is string => Boolean(code),
		)
			? maybeOriginalCategoryCodes
			: [];

		// 4. Update the filter UI state to show the classifier-selected categories
		//    as pre-selected.
		setFilterValues((current) => {
			const withoutCategory = current.filter(
				(fv) => fv.id !== CATEGORY_FILTER_ID,
			);
			return [
				...withoutCategory,
				{
					id: CATEGORY_FILTER_ID,
					value: resolvedOptions.map((opt) => ({ facetId: opt.id })),
				},
			];
		});

		// 5. Mark as applied BEFORE history.replace so that when history.replace
		//    triggers CATEGORY_LOADED → updates categoryId → re-runs this effect,
		//    the idempotency guard blocks the second run.
		lastAppliedClassifierSync = serialized;

		// 6. Update the URL to reflect the classifier selection via history.replace
		//    (replaces the current history entry — no push onto the stack, no new
		//    fetch, but does update React Router location).
		updateClassifierUrl({
			selectedCategories,
			resolvedOptions,
			locationSlug: params.locationSlug,
			history,
		});

		// 7. Track the classifier-driven category change.
		// originalCategoryCode sources:
		//   - Single-category page (params.categorySlug present): Redux categoryCode (e.g. 'WPH')
		//   - Multi-category page (no categorySlug, but pre-selected filterValues from redirect
		//     URL): comma-separated codes of the originally selected categories (e.g. 'WPH,VID')
		//   - All-categories page (no categorySlug, no pre-selected categories): null
		//   - Partial resolution (some pre-selected IDs unmatched): null
		track({
			event: 'Classifier Category Selected',
			properties: {
				action: 'classifier_category_selected',
				classifierVariant: experimentAssignment ?? null,
				originalCategoryCode: params.categorySlug
					? categoryCode
					: originalCategoryCodes.length > 0
						? originalCategoryCodes.join(',')
						: null,
				classifierSelectedCategories: selectedCategories,
			},
		});
	}, [
		selectedCategories,
		categoryId,
		categoryCode,
		params.categorySlug,
		filterConfigs,
		setFilterValues,
		skipNextFetchRef,
		jotaiStore,
		track,
		experimentAssignment,
		params.locationSlug,
		history,
	]);
};

interface UpdateClassifierUrlParams {
	selectedCategories: string[];
	resolvedOptions: Array<{
		singular: { slug: string };
		plural: { slug: string };
	}>;
	locationSlug: string | undefined;
	history: { replace: (path: string) => void };
}

/**
 * Updates the browser URL via React Router's `history.replace` to reflect the
 * classifier's category selection.
 *
 * - Single classifier category → changes the path to the category's canonical
 *   marketplace URL: `/marketplace/{categorySlug}-{locationSlug}` preserving
 *   non-category query params (e.g. `sort`, `q`) and stripping `category=`.
 * - Multiple classifier categories → uses the location-only route as the base
 *   (`/marketplace/{locationSlug}`) and appends `?category={slug1}+{slug2}`,
 *   matching the established URL convention for cross-category pages.
 */
function updateClassifierUrl({
	selectedCategories,
	resolvedOptions,
	locationSlug,
	history,
}: UpdateClassifierUrlParams) {
	if (typeof window === 'undefined' || !locationSlug) {
		return;
	}

	if (selectedCategories.length === 1) {
		// For a single classifier category, build the canonical marketplace URL
		// using the option's plural slug (e.g. "wedding-videographers").
		// Use history.replace so React Router's location stays in sync — this
		// ensures subsequent filter changes use the updated pathname.
		const newCategorySlug = resolvedOptions[0]?.plural?.slug;

		if (!newCategorySlug) {
			return;
		}

		// Preserve non-category query params (e.g. sort=, q=) but strip any
		// stale category= param — it does not belong on single-category URLs.
		const searchParams = new URLSearchParams(window.location.search);
		searchParams.delete('category');
		const qs = searchParams.toString();
		history.replace(
			`/marketplace/${newCategorySlug}-${locationSlug}${qs ? `?${qs}` : ''}`,
		);
	} else {
		// Multi-category: use the singular slug for each resolved option. Join
		// with a space so URLSearchParams.toString() produces literal '+'
		// separators in the URL (standard form-encoding), matching the parser in
		// get-applied-filter.ts which splits on ' ' after URL-decoding.
		// Use the location-only base (/marketplace/{locationSlug}) — the
		// established convention for cross-category URLs — rather than the
		// current single-category pathname, which would diverge from how
		// updateHistory in FilterPills builds multi-category URLs.
		const slugValue = resolvedOptions
			.map((opt) => opt.singular.slug)
			.filter(Boolean)
			.join(' ');

		if (!slugValue) {
			return;
		}

		const searchParams = new URLSearchParams(window.location.search);
		searchParams.set('category', slugValue);
		history.replace(`/marketplace/${locationSlug}?${searchParams.toString()}`);
	}
}
